// src/components/ProtectedRoute.tsx
import { useState, useEffect, ReactNode } from 'react';
import { Loader2, Shield, Lock, AlertCircle } from 'lucide-react';

interface ProtectedRouteProps {
  children: ReactNode;
}

// Simple login component shown for unauthorized routes
function SimpleAdminLogin() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    if (!username || !password) {
      setError('Please enter both username and password');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://janet-marketing-backend-production.up.railway.app/api'}/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }

      // Save token and user info
      sessionStorage.setItem('adminToken', data.data.token);
      sessionStorage.setItem('adminUsername', data.data.username);
      sessionStorage.setItem('adminRole', data.data.role);

      // Redirect to dashboard
      window.location.href = '/admin/dashboard';
    } catch (err: any) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-teal-50 p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl">
            <Shield className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Janet Marketing</h1>
          <p className="text-gray-500 mt-2">Admin Portal</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-xl shadow-2xl border-0 p-6">
          <div className="space-y-4">
            {/* Unauthorized Warning */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <Lock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-900">Authentication Required</p>
                <p className="text-xs text-amber-700 mt-1">
                  You must be logged in to access admin pages. Please sign in below.
                </p>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-600" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="Enter username"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="Enter password"
                onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
              />
            </div>

            <button
              onClick={handleSubmit}
              disabled={isLoading}
              className="w-full bg-teal-500 hover:bg-teal-600 text-white py-3 rounded-lg font-semibold disabled:opacity-50 transition-all shadow-lg hover:shadow-xl disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <Shield className="w-5 h-5" />
                  Sign In
                </>
              )}
            </button>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-xs text-center text-gray-500">
              🔐 Secure admin access • JWT authentication • Session timeout: 8 hours
            </p>
          </div>
        </div>

        {/* Default Credentials Info */}
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-xs text-amber-800 font-medium mb-2">
            🔑 Default Credentials:
          </p>
          <div className="space-y-1 text-xs text-amber-700">
            <p>• Marketing: <code className="bg-amber-100 px-1 rounded">marketingadmin / admin@2026</code></p>
            <p>• Super: <code className="bg-amber-100 px-1 rounded">superadmin / admin@2026</code></p>
            <p>• Developer: <code className="bg-amber-100 px-1 rounded">mdaucodes910 / Mr. Mdau910</code></p>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * ✅ FIXED ProtectedRoute Component - Default Export
 * 
 * IMPROVEMENTS:
 * - Fixed: Now uses default export (not named export)
 * - Shows login page instead of 404 for unauthorized access
 * - Beautiful loading state while checking auth
 * - Integrated with real backend
 * 
 * BEHAVIOR:
 * - Loading: Shows animated shield with spinner
 * - Not Authenticated: Shows login page with warning
 * - Authenticated: Shows protected content
 */
export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const token = sessionStorage.getItem('adminToken');
      
      if (!token) {
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      try {
        // Verify token is valid by calling session-info endpoint
        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://janet-marketing-backend-production.up.railway.app/api'}/admin/session-info`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          setIsAuthenticated(true);
        } else {
          // Token invalid - clear session
          sessionStorage.removeItem('adminToken');
          sessionStorage.removeItem('adminUsername');
          sessionStorage.removeItem('adminRole');
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Show elegant loading state while verifying authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-teal-50 dark:from-gray-900 dark:via-gray-800 dark:to-teal-900">
        <div className="text-center space-y-4">
          {/* Animated shield icon */}
          <div className="relative">
            <div className="absolute inset-0 bg-teal-500/20 rounded-full blur-xl animate-pulse" />
            <div className="relative w-20 h-20 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto shadow-xl">
              <Shield className="w-10 h-10 text-white animate-pulse" />
            </div>
          </div>
          
          {/* Loading spinner */}
          <Loader2 className="w-8 h-8 animate-spin text-teal-500 mx-auto" />
          
          {/* Loading text */}
          <div className="space-y-1">
            <p className="text-gray-900 dark:text-white font-semibold text-lg">
              Verifying Authentication
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Please wait while we validate your session...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ✅ KEY IMPROVEMENT: Show login page instead of 404
  if (!isAuthenticated) {
    console.warn('⚠️ Unauthorized access attempt - showing login page');
    return <SimpleAdminLogin />;
  }

  // ✅ User is authenticated - render protected content
  return <>{children}</>;
}

// ✅ Also export as named export for flexibility
export { ProtectedRoute };